import { Box, HStack, IndicatorDot, IndicatorDotVariants, Text, Tooltip, Z_INDEX } from '@talos/kyoko';
import type { ColDef, ICellRendererParams, IRowNode, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { DefaultMarketAccount } from '../../DefaultMarketAccount/DefaultMarketAccount';
import { useMarketSelectorContext } from '../MarketSelectorContext';
import { AvailabilityEnum } from '../types';
import { isMarketSelectorItemSelectable } from '../utils/items';
import type { MarketSelectorRow } from './types';

type MarketColumnValue = string;

export const marketSelectorAutoGroupColumnDef = {
  headerName: 'Markets',
  sortable: true,
  comparator: marketsColumnComparator,
  sort: 'asc',
  valueGetter: (params: ValueGetterParams<MarketSelectorRow>): MarketColumnValue | undefined => {
    // FYI this valueGetter is only ran for leaf rows. AgGrid automatically takes the grouping key for group rows.
    return params.node?.data?.item.name;
  },
  valueFormatter: ({ value, context }: ValueFormatterParams<MarketSelectorRow, MarketColumnValue>) => {
    if (!value) {
      return '';
    }

    return (
      context.current.marketDisplayNameByName?.get(value) ??
      context.current.marketAccountsByName?.get(value)?.DisplayName ??
      value
    );
  },
  cellRendererParams: (params: ICellRendererParams) => ({
    innerRenderer: MarketSelectorMarketCellRenderer,
    suppressCount: true,
    ...params,
  }),
} satisfies ColDef;

const MarketSelectorMarketCellRenderer = ({
  value,
  context,
  node,
  data,
}: ICellRendererParams<MarketSelectorRow, MarketColumnValue>) => {
  const { itemsMap, itemsByMarket } = useMarketSelectorContext();
  if (!value) {
    return 'Unknown';
  }

  const isGroup = node.group;
  if (isGroup) {
    const selectable = itemsByMarket.get(value)?.some(item => isMarketSelectorItemSelectable(item));
    const label = context.current.marketDisplayNameByName?.get(value) ?? value;

    return (
      <HStack color={selectable ? 'colorTextImportant' : 'colorTextSubtle'} gap="spacing4">
        <Text>{label}</Text>
        {!selectable && (
          <Tooltip
            tooltip="No Accounts selectable within Market"
            usePortal
            interactive={false}
            zIndex={Z_INDEX.dropdown + 1}
          >
            <Box pl="spacing4">
              <IndicatorDot variant={IndicatorDotVariants.Warning} show size={8} />
            </Box>
          </Tooltip>
        )}
      </HStack>
    );
  }

  const item = itemsMap.get(value);
  if (!item) {
    return 'Unknown';
  }

  const availabilityContent =
    item.availability.availability < AvailabilityEnum.Ok ? item.availability.render?.() : undefined;

  const isOnlyChild = (node?.parent?.childrenAfterGroup?.length ?? 1) === 1;

  return (
    <HStack color={isMarketSelectorItemSelectable(item) ? 'colorTextImportant' : 'colorTextSubtle'} gap="spacing4">
      <Text>{item.displayName}</Text>
      {availabilityContent && (
        <Tooltip
          tooltip={availabilityContent}
          usePortal
          interactive={false}
          zIndex={Z_INDEX.dropdown + 1}
          tooltipTestID="market-selector-panel-row-warning-tooltip"
        >
          <Box pl="spacing4" data-testid="market-selector-panel-row-warning">
            <IndicatorDot variant={IndicatorDotVariants.Warning} show size={8} />
          </Box>
        </Tooltip>
      )}
      {!isGroup && !isOnlyChild && <DefaultMarketAccount marketAccount={item.name} />}
    </HStack>
  );
};

function marketsColumnComparator(
  _valueA: unknown,
  _valueB: unknown,
  nodeA: IRowNode<MarketSelectorRow>,
  nodeB: IRowNode<MarketSelectorRow>
) {
  const itemA = nodeA.data?.item;
  const itemB = nodeB.data?.item;
  return (
    (itemA?.displayName ?? '').localeCompare(itemB?.displayName ?? '') ||
    (itemA?.name ?? '').localeCompare(itemB?.name ?? '')
  );
}

/**
 * When our node is a market parent row, as in we are a market row with account children within us,
 * we are selectable if at least one of our children is selectable.
 */
export function isMarketParentRowSelectable(node: IRowNode<MarketSelectorRow>): boolean {
  const children = node.allLeafChildren ?? [];
  return children.some(child => (child.data?.item ? isMarketSelectorItemSelectable(child.data.item) : false));
}
